import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * tailwind-merge has to be told about our custom scales, or it silently drops classes.
 *
 * `text-*` is ambiguous in Tailwind: it is both font size and text color. tailwind-merge
 * resolves that with a built-in list of font-size names, and anything unrecognised falls into
 * the *color* group. Our scale is entirely custom (`text-body-md`, `text-num`, …), so out of
 * the box `cn('text-ink-inverse', 'text-body-md')` treated both as colors, kept the last one,
 * and threw the color away — which is how the primary button shipped as cream text on a cream
 * background. `shadow-*` splits the same way between box shadow and shadow color.
 *
 * Keep these lists in sync with `theme.extend.fontSize` / `theme.extend.boxShadow` in
 * tailwind.config.js.
 */
const FONT_SIZES = [
  'display-1',
  'display-2',
  'title',
  'heading',
  'body-md',
  'body',
  'label',
  'caption',
  'micro',
  'num-lg',
  'num',
  'num-sm',
  'num-xs',
] as const;

const SHADOWS = ['sm', 'popover', 'modal', 'toast', 'focus'] as const;

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: [...FONT_SIZES] }],
      shadow: [{ shadow: [...SHADOWS] }],
    },
  },
});

/**
 * Conditional class names with Tailwind conflict resolution.
 * `cn('p-2', condition && 'p-4')` → `'p-4'`.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
